import { css } from '@emotion/react';
import _ from 'lodash';
import { useMemo } from 'react';

import IconChevronDown from '~icons/ci/chevron-down';

import { MenuButton } from '@adflow/frontend/ui-common';
import { Checkbox } from '@adflow/frontend/ui-form';
import { su, theme } from '@adflow/frontend/ui-theme';

import { useDataTableMeta } from './useDataTableMeta';

const styles = {
	checkboxWrapper: (checked: boolean) =>
		css(
			{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				paddingTop: '0.40rem',
				paddingRight: '0.50rem',
				cursor: 'pointer',
				height: '1.75rem',
				position: 'absolute',
				zIndex: 1,
			},
			checked && { backgroundColor: theme.colors.background.tableHeader }
		),
};

interface DataTableSelectorButtonProps<T> {
	items: T[];
	getId: (item: T) => number | string;
	isDisabled?: (item: T) => boolean;
}
export function DataTableSelectorButton<T>({
	items,
	getId,
	isDisabled,
}: DataTableSelectorButtonProps<T>) {
	const { selected, setSelected } = useDataTableMeta();

	// Filter out disabled items
	const enabledItems = useMemo(
		() => (isDisabled ? items.filter((item) => !isDisabled(item)) : items),
		[items, isDisabled]
	);

	const checked = useMemo(() => {
		if (enabledItems.length === 0) return false;
		return !!(
			selected.length > 0 &&
			enabledItems.every((item) => selected.includes(getId(item)))
		);
	}, [enabledItems, getId, selected]);

	return (
		<div css={styles.checkboxWrapper(!!selected.length)}>
			<Checkbox
				onChange={() => {
					if (checked) {
						setSelected([]);
					} else {
						// Only select enabled items
						setSelected((prevSelected) =>
							_.uniq([...prevSelected, ...enabledItems.map((i) => getId(i))])
						);
					}
				}}
				checked={checked}
			/>

			{selected.length ? (
				<MenuButton
					css={[
						su.roundedFull,
						{
							color: theme.colors.white,
							backgroundColor: theme.colors.text.tableHeader,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							paddingLeft: '0.25rem',
							paddingRight: '0.25rem',
							marginLeft: '0.5rem',
							cursor: 'pointer',
							height: '1.75rem',
						},
					]}
					data-testid="datatable-selector-button"
				>
					{selected.length}
					<IconChevronDown
						css={{ marginLeft: -2, marginRight: -2 }}
						width={18}
						height={18}
					/>
				</MenuButton>
			) : null}
		</div>
	);
}
